// Daily Wisdom: one quote a day chosen from your own signals, plus a browsable library.
//
// Nothing here is encrypted — the catalog is public text and a saved quote reveals only that you
// liked a published line, not what you wrote. Keeping it in plaintext means the library is searchable
// and the daily selection can run server-side against your archetype weights.

import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface Quote {
  /** Present on saved quotes; absent on a catalog pick until you save it. */
  id?: string;
  catalogId: string | null;
  text: string;
  author: string | null;
  source: string | null;
  /** A short phrase the catalog marks for emphasis, rendered in italic serif. */
  emphasis: string | null;
  category: string;
  origin: 'daily' | 'discover' | 'custom';
}

export interface DailyWisdom {
  quote: Quote | null;
  /** Why this line today, in Oreia's voice. Empty until there are enough signals. */
  reason: string;
  updatesInSeconds: number;
}

interface CatalogRow {
  id?: string | null;
  catalog_id?: string | null;
  text?: string | null;
  author?: string | null;
  source?: string | null;
  emphasis?: string | null;
  category?: string | null;
  origin?: string | null;
}

function toQuote(row: CatalogRow, fallbackOrigin: Quote['origin']): Quote {
  return {
    id: row.id ?? undefined,
    catalogId: row.catalog_id ?? row.id ?? null,
    text: (row.text ?? '').trim(),
    author: row.author?.trim() || null,
    source: row.source?.trim() || null,
    emphasis: row.emphasis?.trim() || null,
    category: row.category?.trim() || 'Saved',
    origin: (row.origin as Quote['origin']) ?? fallbackOrigin,
  };
}

export async function fetchDailyWisdom(signal?: AbortSignal): Promise<DailyWisdom> {
  const res = await apiGet<{
    quote?: CatalogRow | null;
    reason?: string;
    updates_in_seconds?: number;
  }>('/api/daily-wisdom/today', signal);

  return {
    // The catalog row's own id is the catalog id — saving sends it so the server can hydrate.
    quote: res.quote ? toQuote({ ...res.quote, id: undefined, catalog_id: res.quote.id }, 'daily') : null,
    reason: res.reason?.trim() ?? '',
    updatesInSeconds: Number(res.updates_in_seconds ?? 0),
  };
}

export const DISCOVER_MOODS = [
  'inspirational',
  'love',
  'wisdom',
  'courage',
  'solitude',
  'change',
  'grief',
  'hope',
] as const;

export async function fetchDiscover(mood: string, signal?: AbortSignal): Promise<Quote[]> {
  const res = await apiGet<{ quotes?: CatalogRow[] }>(
    `/api/daily-wisdom/discover?mood=${encodeURIComponent(mood)}`,
    signal,
  );
  return (res.quotes ?? [])
    .map((row) => toQuote({ ...row, category: row.category ?? mood }, 'discover'))
    .filter((q) => q.text.length > 0);
}

export interface Library {
  categories: string[];
  quotes: Quote[];
}

export async function fetchLibrary(signal?: AbortSignal): Promise<Library> {
  const res = await apiGet<{ categories?: string[]; quotes?: CatalogRow[] }>(
    '/api/daily-wisdom/library',
    signal,
  );
  return {
    categories: res.categories ?? [],
    quotes: (res.quotes ?? []).map((row) => toQuote(row, 'custom')),
  };
}

/** Save a quote to the library. Returns the stored row, which now carries an id. */
export async function saveQuote(quote: Quote): Promise<Quote> {
  const res = await apiPost<{ quote?: CatalogRow }>('/api/daily-wisdom/library', {
    catalog_id: quote.catalogId,
    text: quote.text,
    author: quote.author,
    source: quote.source,
    emphasis: quote.emphasis,
    category: quote.category,
    origin: quote.origin,
  });
  return res.quote ? toQuote(res.quote, quote.origin) : quote;
}

export async function removeQuote(id: string): Promise<void> {
  await apiDelete(`/api/daily-wisdom/library/${encodeURIComponent(id)}`);
}

/** Move a saved quote into a different collection. */
export async function recategorizeQuote(id: string, category: string): Promise<void> {
  await apiPatch(`/api/daily-wisdom/library/${encodeURIComponent(id)}`, { category });
}
